// ✅B-2（差し替え確定版 / そのまま貼れる）：Estimate-form.ts（全文）
// 方針
// - 各機能をクラス分割（住所 / 日時 / 家財 / オプション / 資材 / 合計）
// - 家財は「全部 group 形式」で持てる（variants が1件でもOK）
// - unit_price が 0 の場合は表示しない（将来価格が決まったら数字を入れるだけ）
// - 家財金額は hidden（.js-items-total-price）に保持し、grand_total に加算（現仕様でもOK）

type AddrTarget = 'pickup' | 'delivery';

type ZipCloudResponse = {
  status: number;
  message: string | null;
  results: Array<{
    zipcode: string;
    address1: string;
    address2: string;
    address3: string;
  }> | null;
};

/**
 * // Class Obj: 住所自動入力
 */
class EstimateFormAddressAutoFill
{
  private readonly mRoot: HTMLElement;
  private readonly mEndpoint: string = 'https://zipcloud.ibsnet.co.jp/api/search';

  // Constructor: コンストラクタ
  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  // Method: 初期化
  public init(): void
  {
    // クリック（住所検索ボタン）
    this.mRoot.addEventListener('click', (ev: MouseEvent) =>
    {
      const target_elem = ev.target as HTMLElement | null;
      if (!target_elem) return;

      const btn = target_elem.closest('.js-addr-search') as HTMLButtonElement | null;
      if (!btn) return;

      ev.preventDefault();

      const addr_target = (btn.dataset.target ?? '') as AddrTarget;
      if (addr_target !== 'pickup' && addr_target !== 'delivery') return;

      this.fetchAndFill(addr_target, { show_validation_error: true }).catch((err: unknown) =>
      {
        this.showError(addr_target, '住所検索に失敗しました。郵便番号をご確認ください。');
        // eslint-disable-next-line no-console
        console.error(err);
      });
    });

    // blur（郵便番号入力からフォーカスアウトで自動検索したい場合）
    this.mRoot.addEventListener('blur', (ev: FocusEvent) =>
    {
      const input = ev.target as HTMLInputElement | null;
      if (!input) return;

      if (input.id === 'pickup_postcode')
      {
        this.fetchAndFill('pickup', { show_validation_error: false }).catch(() => void 0);
      }

      if (input.id === 'delivery_postcode')
      {
        this.fetchAndFill('delivery', { show_validation_error: false }).catch(() => void 0);
      }
    }, true);
  }

  // Method: 郵便番号から住所を自動入力
  private async fetchAndFill(
    addr_target: AddrTarget,
    options: { show_validation_error: boolean; }
  ): Promise<void>
  {
    const postcode_input = this.mRoot.querySelector<HTMLInputElement>(addr_target === 'pickup'
      ? '#pickup_postcode'
      : '#delivery_postcode'
    );
    const address_input = this.mRoot.querySelector<HTMLInputElement>(addr_target === 'pickup'
      ? '#pickup_address'
      : '#delivery_address'
    );

    if (!postcode_input || !address_input) return;

    const postcode_raw = (postcode_input.value ?? '').trim();
    const zip_code = postcode_raw.replace(/[^0-9]/g, '');

    this.clearError(addr_target);

    // 住所検索ボタン押下時は、未入力/桁不足をエラー表示
    if (options.show_validation_error)
    {
      if (zip_code.length === 0)
      {
        this.showError(addr_target, '郵便番号を入力してください。');
        return;
      }

      if (zip_code.length !== 7)
      {
        this.showError(addr_target, '郵便番号は7桁で入力してください（例：8600000）。');
        return;
      }
    } else
    {
      // blur 等は途中入力を想定：7桁になったら実行
      if (zip_code.length !== 7) return;
    }

    const url = `${this.mEndpoint}?zipcode=${encodeURIComponent(zip_code)}`;
    const res = await fetch(url, { method: 'GET' });
    const data: ZipCloudResponse = await res.json();

    if (data.status !== 200 || !data.results || data.results.length === 0)
    {
      this.showError(addr_target, '住所が見つかりませんでした（郵便番号をご確認ください）。');
      return;
    }

    const first = data.results[0];
    const address_text = `${first.address1}${first.address2}${first.address3}`;
    address_input.value = address_text;

    // 後続処理用イベント（距離計算など将来拡張用）
    this.mRoot.dispatchEvent(new CustomEvent('ef:addressUpdated', {
      detail: { addr_target, zip_code, address_text },
    }));
  }

  // Method: エラーメッセージ表示
  private showError(addr_target: AddrTarget, message: string): void
  {
    const host = this.mRoot.querySelector<HTMLElement>(addr_target === 'pickup'
      ? '.js-pickup-error'
      : '.js-delivery-error'
    );

    if (!host)
    {
      // eslint-disable-next-line no-alert
      alert(message);
      return;
    }

    host.textContent = message;
    host.hidden = false;
  }

  // Method: エラーメッセージクリア
  private clearError(addr_target: AddrTarget): void
  {
    const host = this.mRoot.querySelector<HTMLElement>(addr_target === 'pickup'
      ? '.js-pickup-error'
      : '.js-delivery-error'
    );

    if (!host) return;

    host.textContent = '';
    host.hidden = true;
  }
}

// flatpickr（方法B：グローバル読み込み）
declare const flatpickr: any;

type ScheduleState = {
  date_iso: string | null;      // 'YYYY-MM-DD'
  date_display: string | null;  // 'YYYY/MM/DD'
  time_range: string | null;    // '09:00-12:00'
  time_fee: number;             // 円
};

/**
 * // Class Obj: 日程入力
 */
class EstimateFormSchedulePicker
{
  private readonly mRoot: HTMLElement;

  private mModal: HTMLElement | null = null;
  private mDateUi: HTMLInputElement | null = null;

  private mHiddenDate: HTMLInputElement | null = null;
  private mHiddenTime: HTMLInputElement | null = null;
  private mHiddenFee: HTMLInputElement | null = null;

  private mDisplay: HTMLElement | null = null;

  private mState: ScheduleState = {
    date_iso: null,
    date_display: null,
    time_range: null,
    time_fee: 0,
  };

  // Constructor: コンストラクタ
  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  // Method: 初期化
  public init(): void
  {
    this.mModal = this.mRoot.querySelector<HTMLElement>('.js-schedule-modal');
    this.mDateUi = this.mRoot.querySelector<HTMLInputElement>('#efPickupDateUi');

    this.mHiddenDate = this.mRoot.querySelector<HTMLInputElement>('.js-pickup-date');
    this.mHiddenTime = this.mRoot.querySelector<HTMLInputElement>('.js-pickup-time');
    this.mHiddenFee = this.mRoot.querySelector<HTMLInputElement>('.js-pickup-time-fee');

    this.mDisplay = this.mRoot.querySelector<HTMLElement>('.js-schedule-display');

    if (!this.mModal || !this.mDateUi || !this.mHiddenDate || !this.mHiddenTime || !this.mHiddenFee || !this.mDisplay)
    {
      return;
    }

    // open / close / time / apply（イベント委譲）
    this.mRoot.addEventListener('click', (ev: MouseEvent) =>
    {
      const target_elem = ev.target as HTMLElement | null;
      if (!target_elem) return;

      const open_btn = target_elem.closest('.js-open-schedule') as HTMLButtonElement | null;
      if (open_btn)
      {
        ev.preventDefault();
        this.open();
        return;
      }

      const close_btn = target_elem.closest('[data-close="1"]') as HTMLElement | null;
      if (close_btn)
      {
        ev.preventDefault();
        this.close();
        return;
      }

      // const time_btn = target_elem.closest('.js-pickup-time-btn') as HTMLButtonElement | null;
      // if (time_btn)
      // {
      //   ev.preventDefault();
      //   this.selectTimeButton(time_btn);
      //   return;
      // }

      const time_btn = target_elem.closest('.js-pickup-time-btn') as HTMLElement | null;
      if (time_btn)
      {
        // まず：label 内の radio を探す（radio化した場合）
        const radio = time_btn.querySelector<HTMLInputElement>('input[type="radio"][name="pickup_time_choice"]');

        if (radio)
        {
          // label の標準挙動を邪魔しないのが理想だが、
          // 既存ロジック都合で click を拾っているので、ここで確実に checked にする
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));

          // time_range を更新してハイライト等に使う（data-time を維持している前提）
          this.selectTimeButton(time_btn);
          return;
        }

        // button時代のフォールバック（buttonが残っている場合）
        ev.preventDefault();
        this.selectTimeButton(time_btn);
        return;
      }


      const apply_btn = target_elem.closest('.js-apply-schedule') as HTMLButtonElement | null;
      if (apply_btn)
      {
        ev.preventDefault();
        this.apply();
        this.close();
        return;
      }
    });

    // flatpickr init（inline大型カレンダー）
    flatpickr(this.mDateUi, {
      inline: true,
      static: true,
      dateFormat: 'Y-m-d',
      disableMobile: true,
      onChange: (selected_dates: Date[]) =>
      {
        if (!selected_dates || selected_dates.length === 0) return;

        const d = selected_dates[0];
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');

        this.mState.date_iso = `${yyyy}-${mm}-${dd}`;
        this.mState.date_display = `${yyyy}/${mm}/${dd}`;

        this.renderDisplay(true);
      },
    });

    // 初期：hidden から復元して表示
    this.restoreFromHidden();
    this.highlightTimeButtons();
    this.renderDisplay(false);
  }

  // Method: 開く
  private open(): void
  {
    if (!this.mModal) return;

    this.mModal.hidden = false;
    this.mModal.setAttribute('aria-hidden', 'false');

    this.restoreFromHidden();
    this.highlightTimeButtons();
    this.renderDisplay(true);
  }

  // Method: 閉じる
  private close(): void
  {
    if (!this.mModal) return;

    this.mModal.hidden = true;
    this.mModal.setAttribute('aria-hidden', 'true');
  }

  // Method: hidden から復元
  private restoreFromHidden(): void
  {
    if (!this.mHiddenDate || !this.mHiddenTime || !this.mHiddenFee) return;

    const date_iso = (this.mHiddenDate.value ?? '').trim() || null;
    const time_range = (this.mHiddenTime.value ?? '').trim() || null;
    const time_fee = Number(this.mHiddenFee.value ?? '0');

    this.mState.date_iso = date_iso;

    // display 用（YYYY-MM-DD -> YYYY/MM/DD）
    this.mState.date_display = date_iso ? date_iso.replaceAll('-', '/') : null;

    this.mState.time_range = time_range;
    this.mState.time_fee = Number.isFinite(time_fee) ? time_fee : 0;
  }

  // Method: 時間ボタンを選択
  // private selectTimeButton(btn: HTMLButtonElement): void
  // {
  //   const time_range = (btn.dataset.time ?? '').trim();
  //   const fee = Number(btn.dataset.fee ?? '0');

  //   this.mState.time_range = time_range || null;
  //   this.mState.time_fee = Number.isFinite(fee) ? fee : 0;

  //   this.highlightTimeButtons();
  //   this.renderDisplay(true);
  // }
  private selectTimeButton(time_btn: HTMLElement): void
  {
    const time_range = (time_btn.dataset.time ?? '').trim();
    const fee = (time_btn.dataset.fee ?? '0').trim();

    this.mState.time_range = time_range !== '' ? time_range : null;
    // this.mState.time_fee = fee !== '' ? fee : '0';

    this.highlightTimeButtons();
  }


  // Method: 選択中の時間ボタンをハイライト
  // private highlightTimeButtons(): void
  // {
  //   const btns = this.mRoot.querySelectorAll<HTMLButtonElement>('.js-pickup-time-btn');
  //   btns.forEach((b) =>
  //   {
  //     const t = (b.dataset.time ?? '').trim();
  //     const is_active = this.mState.time_range !== null && t === this.mState.time_range;
  //     b.classList.toggle('is_active', is_active);
  //   });
  // }

  // Method: 選択中の時間ボタンをハイライト
  private highlightTimeButtons(): void
  {
    const btns = this.mRoot.querySelectorAll<HTMLElement>('.js-pickup-time-btn');
    btns.forEach((b) =>
    {
      const t = (b.dataset.time ?? '').trim();
      const is_active = this.mState.time_range !== null && t === this.mState.time_range;
      b.classList.toggle('is_active', is_active);

      // radioがある場合は checked も同期（任意だがズレ防止になる）
      const radio = b.querySelector<HTMLInputElement>('input[type="radio"][name="pickup_time_choice"]');
      if (radio)
      {
        radio.checked = is_active;
      }
    });
  }


  // Method: 選択状態を適用
  private apply(): void
  {
    if (!this.mHiddenDate || !this.mHiddenTime || !this.mHiddenFee) return;

    this.mHiddenDate.value = this.mState.date_iso ?? '';
    this.mHiddenTime.value = this.mState.time_range ?? '';
    this.mHiddenFee.value = String(this.mState.time_fee ?? 0);

    this.renderDisplay(false);

    this.mRoot.dispatchEvent(new CustomEvent('ef:scheduleUpdated', {
      detail: { ...this.mState },
    }));
  }

  // Method: 選択状態を表示
  private renderDisplay(is_preview: boolean): void
  {
    if (!this.mDisplay) return;

    const date = this.mState.date_display;
    const time = this.mState.time_range;

    if (!date && !time)
    {
      this.mDisplay.textContent = '未選択';
      return;
    }

    if (date && !time)
    {
      this.mDisplay.textContent = is_preview ? `${date}（時間帯未選択）` : `${date}`;
      return;
    }

    if (!date && time)
    {
      this.mDisplay.textContent = is_preview ? `（日付未選択）${time}` : `${time}`;
      return;
    }

    this.mDisplay.textContent = `${date} ${time}`;
  }
}

// ==============================
// AREA: 家財カタログ（全部 group 形式 / variants 1件でもOK）
// ==============================
type ItemCategory = 'appliance' | 'furniture' | 'box' | 'other';

type CatalogVariant = {
  id: string;
  label: string;      // ひとまず 'オプション' でOK
  unit_price: number; // 0なら表示しない
};

type CatalogGroupEntry = {
  kind: 'group';
  name: string;
  variants: CatalogVariant[];
};

type Catalog = Record<ItemCategory, { label: string; items: CatalogGroupEntry[]; }>;

const ITEM_CATALOG: Catalog = {
  appliance: {
    label: '家電',
    items: [
      {
        kind: 'group',
        name: '液晶テレビ',
        variants: [
          { id: 'appliance_tv40_inch_or_less', label: '40インチ以下', unit_price: 0 },
          { id: 'appliance_tv60_inch_or_less', label: '60インチ以下', unit_price: 0 },
          { id: 'appliance_tv75_inch_or_less', label: '75インチ以下', unit_price: 0 },
          { id: 'appliance_tv75_inch_or_more', label: '75インチ以上', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '洗濯機',
        variants: [
          { id: 'appliance_washer_type_vertical', label: '縦型洗濯機', unit_price: 0 },
          { id: 'appliance_washer_type_drum_8kg_or_less', label: 'ドラム式洗濯機8kg以下', unit_price: 0 },
          { id: 'appliance_washer_type_drum_9kg_or_more', label: 'ドラム式洗濯機9kg以上', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '冷蔵庫',
        variants: [
          { id: 'appliance_fridge_200l_or_less', label: '200L以下', unit_price: 0 },
          { id: 'appliance_fridge_350l_or_less', label: '350L以下', unit_price: 0 },
          { id: 'appliance_fridge_351l_or_more', label: '351L以上', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'レンジ・トースター',
        variants: [
          { id: 'appliance_microwave', label: '電子レンジ', unit_price: 0 },
          { id: 'appliance_toaster', label: 'トースター', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '掃除機',
        variants: [
          { id: 'appliance_cleaner_type_canister', label: 'キャニスター型掃除機', unit_price: 0 },
          { id: 'appliance_toaster_type_stick', label: 'スティック型掃除機', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '空気清浄機',
        variants: [
          { id: 'appliance_air_purifier', label: '空気清浄機', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'ファンヒーター',
        variants: [
          { id: 'appliance_fan_heater', label: 'ファンヒーター', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '扇風機',
        variants: [
          { id: 'appliance_fan-electric', label: '扇風機', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'エアコン',
        variants: [
          { id: 'appliance_air_conditioner', label: 'エアコン', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '照明',
        variants: [
          { id: 'appliance_lighting', label: '照明', unit_price: 0 },
        ]
      },
    ],
  },
  furniture: {
    label: '家具',
    items: [
      {
        kind: 'group',
        name: 'テレビ台',
        variants: [
          { id: 'furniture_tv_rak_120cm_or_less', label: '120cm以下', unit_price: 0 },
          { id: 'furniture_tv_rak_150cm_or_less', label: '150cm以下', unit_price: 0 },
          { id: 'furniture_tv_rak_180cm_or_less', label: '180cm以下', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'ダイニングセット',
        variants: [
          { id: 'furniture_dining_table_2person', label: 'ダイニングテーブル(2人用)', unit_price: 0 },
          { id: 'furniture_dining_table_4person', label: 'ダイニングテーブル(4人用)', unit_price: 0 },
          { id: 'furniture_dining_chair', label: 'ダイニングチェア', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'ローテーブル',
        variants: [
          { id: 'furniture_low_table', label: 'ローテーブル', unit_price: 0 },
        ],
      },
      {
        kind: 'group',
        name: 'こたつ',
        variants: [
          { id: 'furniture_kotatu', label: 'こたつ', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'デスク',
        variants: [
          { id: 'furniture_desk_l_shaped', label: 'L字デスク(分解込み)', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'ソファー',
        variants: [
          { id: 'furniture_sofa_one_person', label: 'ソファー(1人掛け)', unit_price: 0 },
          { id: 'furniture_sofa_two_person', label: 'ソファー(2人掛け)', unit_price: 0 },
          { id: 'furniture_sofa_three_person', label: 'ソファー(3人掛け)', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '食器棚',
        variants: [
          { id: 'furniture_cupboard_60cm_or_less', label: '食器棚(60cm以下)', unit_price: 0 },
          { id: 'furniture_cupboard_120cm_or_less', label: '食器棚(120cm以下)', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: 'ベッド',
        variants: [
          { id: 'furniture_bed_single', label: 'シングルベッド', unit_price: 0 },
          { id: 'furniture_bed_semi_double', label: 'セミダブルベッド', unit_price: 0 },
          { id: 'furniture_bed_double', label: 'ダブルベッド', unit_price: 0 },
        ],
      },
      {
        kind: 'group',
        name: 'マットレス',
        variants: [
          { id: 'furniture_mattress_sinble', label: 'シングルマットレス', unit_price: 0 },
          { id: 'furniture_mattress_semi_double', label: 'セミダブルマットレス', unit_price: 0 },
          { id: 'furniture_mattress_double', label: 'ダブルマットレス', unit_price: 0 },
          { id: 'furniture_mattress_three_fold', label: '三つ折りマットレス', unit_price: 0 },
        ],
      },
      {
        kind: 'group',
        name: '布団',
        variants: [
          { id: 'furniture_futon', label: '布団', unit_price: 0 },
        ],
      },
      {
        kind: 'group',
        name: 'カラーボックス',
        variants: [
          { id: 'furniture_color_box_two_row', label: 'カラーボックス(2段)', unit_price: 0 },
          { id: 'furniture_color_box_three_row', label: 'カラーボックス(3段)', unit_price: 0 },
        ],
      },
    ],
  },
  box: {
    label: '段ボール',
    items: [
      {
        kind: 'group',
        name: '段ボールS',
        variants: [
          { id: 'cardboard_3_edge_95cm_or_less', label: '3辺合計95cm以下', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '段ボールM',
        variants: [
          { id: 'cardboard_3_edge_112cm_or_less', label: '3辺合計112cm以下', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '段ボールL',
        variants: [
          { id: 'cardboard_3_edge_140cm_or_less', label: '3辺合計140cm以下', unit_price: 0 },
        ]
      },
      {
        kind: 'group',
        name: '段ボールLL',
        variants: [
          { id: 'cardboard_3_edge_170cm_or_less', label: '3辺合計170cm以下', unit_price: 0 },
        ]
      },
    ],
  },
  other: {
    label: 'その他',
    items: [
      {
        kind: 'group',
        name: '自転車',
        variants: [
          { id: 'other_bicycle', label: '自転車', unit_price: 0 }
        ]
      },
    ],
  },
};

// ==============================
// 家財（カウンター）
// ==============================
/**
 * // Class Obj: 家財カウンター（※表示/非表示は CollapsePanel 側に委譲）
 */
class EstimateFormItemCounter
{
  private readonly mRoot: HTMLElement;

  private mCountSummaryView: HTMLElement | null = null;

  private mHiddenJson: HTMLInputElement | null = null;
  private mHiddenTotal: HTMLInputElement | null = null;

  private mHiddenItemsSummary: HTMLInputElement | null = null;
  private mHiddenItemsTotalPrice: HTMLInputElement | null = null;

  private mLists: Map<ItemCategory, HTMLElement> = new Map();

  private mItemsTotalView: HTMLElement | null = null;
  private mItemsKadenView: HTMLElement | null = null;
  private mItemsKaguView: HTMLElement | null = null;

  // id -> count
  private mCounts: Map<string, number> = new Map();

  private mHiddenItemsDetailText: HTMLInputElement | null = null;

  // Constructor: コンストラクタ
  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  // Method: 初期化
  public init(): void
  {
    this.mHiddenItemsDetailText = this.mRoot.querySelector<HTMLInputElement>('.js-items-detail-text');

    this.mCountSummaryView = this.mRoot.querySelector<HTMLElement>('.js-items-count-summary-view');

    this.mHiddenJson = this.mRoot.querySelector<HTMLInputElement>('.js-items-json');
    this.mHiddenTotal = this.mRoot.querySelector<HTMLInputElement>('.js-items-count-total');

    this.mHiddenItemsSummary = this.mRoot.querySelector<HTMLInputElement>('.js-items-summary-hidden');
    this.mHiddenItemsTotalPrice = this.mRoot.querySelector<HTMLInputElement>('.js-items-total-price');

    this.mItemsTotalView = this.mRoot.querySelector<HTMLElement>('.js-items-total-view');
    this.mItemsKadenView = this.mRoot.querySelector<HTMLElement>('.js-items-kaden-view');
    this.mItemsKaguView = this.mRoot.querySelector<HTMLElement>('.js-items-kagu-view');

    if (!this.mCountSummaryView || !this.mHiddenJson || !this.mHiddenTotal)
    {
      return;
    }

    // リスト収集
    this.mRoot.querySelectorAll<HTMLElement>('.js-items-list[data-category]').forEach((list) =>
    {
      const category = (list.dataset.category ?? '') as ItemCategory;
      if (!this.isValidCategory(category)) return;
      this.mLists.set(category, list);
    });

    if (this.mLists.size === 0)
    {
      return;
    }

    // hiddenから復元
    this.restoreFromHidden();

    // ✅最初に全カテゴリを描画しておく（開いた瞬間に中身が無い問題を回避）
    (Object.keys(ITEM_CATALOG) as ItemCategory[]).forEach((category) =>
    {
      if (!this.mLists.has(category)) return;
      this.renderList(category);
    });

    // クリック委譲（数量増減のみ）
    this.mRoot.addEventListener('click', (ev: MouseEvent) =>
    {
      const target_elem = ev.target as HTMLElement | null;
      if (!target_elem) return;

      const dec_btn = target_elem.closest('.js-item-dec') as HTMLButtonElement | null;
      if (dec_btn)
      {
        ev.preventDefault();
        const id = (dec_btn.dataset.id ?? '').trim();
        if (!id) return;

        this.changeCount(id, -1);
        return;
      }

      const inc_btn = target_elem.closest('.js-item-inc') as HTMLButtonElement | null;
      if (inc_btn)
      {
        ev.preventDefault();
        const id = (inc_btn.dataset.id ?? '').trim();
        if (!id) return;

        this.changeCount(id, +1);
        return;
      }
    });

    // 初期同期
    this.syncAll();
  }

  // Method: リスト描画
  private renderList(category: ItemCategory): void
  {
    const list = this.mLists.get(category);
    if (!list) return;

    const entries = ITEM_CATALOG[category].items;

    const html = entries.map((entry) =>
    {
      const group_title = `
        <p class="category-name ga-title">${this.escapeHtml(entry.name)}</p>
      `;

      const variants_html = entry.variants.map((v) =>
      {
        const count = this.mCounts.get(v.id) ?? 0;

        const price_html = v.unit_price > 0
          ? `<span class="ef-item-price">${this.formatNumber(v.unit_price)}円</span>`
          : '';

        return `
          <div class="row ef-item-row--variant" data-id="${this.escapeAttr(v.id)}">
            <div class="name">
              <span class="variant">${this.escapeHtml(v.label)}</span>
              ${price_html}
            </div>

            <div class="counter">
              <button type="button" class="btn-decrement js-item-dec" data-id="${this.escapeAttr(v.id)}" aria-label="減らす">
                <span class="symbol">－</span>
              </button>
              <span class="count js-item-count" data-id="${this.escapeAttr(v.id)}">${count}</span>
              <button type="button" class="btn-increment js-item-inc" data-id="${this.escapeAttr(v.id)}" aria-label="増やす">
                <span class="symbol">＋</span>
              </button>
            </div>
          </div>
        `;
      }).join('');

      return `
        <div class="item">
          ${group_title}
          <div class="choices ga-text">
            ${variants_html}
          </div>
        </div>
      `;
    }).join('');

    list.innerHTML = html;
  }

  // Method: 数量変更
  private changeCount(id: string, delta: number): void
  {
    const current = this.mCounts.get(id) ?? 0;
    const next = Math.max(0, current + delta);

    this.mCounts.set(id, next);

    // 表示更新（同一idを全更新）
    const count_elems = this.mRoot.querySelectorAll<HTMLElement>(`.js-item-count[data-id="${CSS.escape(id)}"]`);
    count_elems.forEach((el) =>
    {
      el.textContent = String(next);
    });

    this.syncAll();
  }

  // Method: 同期
  private syncAll(): void
  {
    this.syncHidden();
    this.renderSummaryAndTotals();
  }

  // Method: hidden同期
  private syncHidden(): void
  {
    if (!this.mHiddenJson || !this.mHiddenTotal) return;

    const id_to_meta = this.buildIdIndex();

    const payload: Array<{
      id: string;
      name: string;
      category: ItemCategory;
      category_label: string;
      unit_price: number;
      count: number;
      subtotal: number;
    }> = [];

    let total_count = 0;
    let total_price = 0;

    this.mCounts.forEach((count, id) =>
    {
      if (count <= 0) return;

      const meta = id_to_meta.get(id);
      if (!meta) return;

      const subtotal = count * meta.unit_price;

      payload.push({
        id,
        name: meta.name,
        category: meta.category,
        category_label: meta.category_label,
        unit_price: meta.unit_price,
        count,
        subtotal,
      });

      total_count += count;
      total_price += subtotal;
    });

    this.mHiddenJson.value = JSON.stringify(payload);
    this.mHiddenTotal.value = String(total_count);

    if (this.mHiddenItemsTotalPrice)
    {
      this.mHiddenItemsTotalPrice.value = String(total_price);
    }

    const counts_by_category = this.getCountsByCategory();

    const summary = `家財/計${total_count}点（内、家電${counts_by_category.appliance}点、家具${counts_by_category.furniture}点）`;
    if (this.mHiddenItemsSummary)
    {
      this.mHiddenItemsSummary.value = summary;
    }

    const detail_text = this.buildItemsDetailText(payload);

    if (this.mHiddenItemsDetailText)
    {
      this.mHiddenItemsDetailText.value = detail_text;
    }


    // ✅イベント名は ef に統一
    this.mRoot.dispatchEvent(new CustomEvent('ef:itemsUpdated', {
      detail: {
        items: payload,
        total_count,
        total_price,
        counts_by_category,
        summary,
      },
    }));
  }

  private buildItemsDetailText(payload: Array<{
    id: string;
    name: string;
    category: ItemCategory;
    category_label: string;
    unit_price: number;
    count: number;
    subtotal: number;
  }>): string
  {
    if (!payload || payload.length === 0) return '（未選択）';

    const order: ItemCategory[] = ['appliance', 'furniture', 'box', 'other'];

    const grouped: Record<ItemCategory, Array<{ name: string; count: number; }>> = {
      appliance: [],
      furniture: [],
      box: [],
      other: [],
    };

    payload.forEach((x) =>
    {
      if (!x || !this.isValidCategory(x.category)) return;
      grouped[x.category].push({ name: x.name, count: x.count });
    });

    const lines: string[] = [];

    order.forEach((cat) =>
    {
      const label = ITEM_CATALOG[cat]?.label ?? cat;
      const list = grouped[cat];

      if (!list || list.length === 0) return;

      lines.push(`${label}：`);

      list.forEach((item) =>
      {
        lines.push(`- ${item.name} ×${item.count}`);
      });

      lines.push(''); // 空行
    });

    return lines.join('\n').trim();
  }


  // Method: 合計表示を描画
  private renderSummaryAndTotals(): void
  {
    if (!this.mCountSummaryView) return;

    const total_count = this.getTotalCount();
    const counts_by_category = this.getCountsByCategory();

    // 「選択中：◯点」
    this.mCountSummaryView.textContent = String(total_count);

    // 概算金額の家財内訳（任意）
    if (this.mItemsTotalView) this.mItemsTotalView.textContent = String(total_count);
    if (this.mItemsKadenView) this.mItemsKadenView.textContent = String(counts_by_category.appliance);
    if (this.mItemsKaguView) this.mItemsKaguView.textContent = String(counts_by_category.furniture);
  }

  // Method: 合計を取得
  private getTotalCount(): number
  {
    let total = 0;
    this.mCounts.forEach((v) => { total += v; });
    return total;
  }

  // Method: 合計をカテゴリ別に取得
  private getCountsByCategory(): Record<ItemCategory, number>
  {
    const id_to_meta = this.buildIdIndex();

    const counts: Record<ItemCategory, number> = {
      appliance: 0,
      furniture: 0,
      box: 0,
      other: 0,
    };

    this.mCounts.forEach((count, id) =>
    {
      if (count <= 0) return;
      const meta = id_to_meta.get(id);
      if (!meta) return;

      counts[meta.category] += count;
    });

    return counts;
  }

  // Method: IDとメタ情報のマップを作成
  private buildIdIndex(): Map<string, { name: string; category: ItemCategory; category_label: string; unit_price: number; }>
  {
    const map = new Map<string, { name: string; category: ItemCategory; category_label: string; unit_price: number; }>();

    (Object.keys(ITEM_CATALOG) as ItemCategory[]).forEach((cat) =>
    {
      const cat_label = ITEM_CATALOG[cat].label;

      ITEM_CATALOG[cat].items.forEach((entry) =>
      {
        entry.variants.forEach((v) =>
        {
          // 表示名は「グループ名（ラベル）」にしたければここで変更
          const name = (v.label && v.label !== 'オプション')
            ? `${entry.name}（${v.label}）`
            : entry.name;

          map.set(v.id, {
            name,
            category: cat,
            category_label: cat_label,
            unit_price: Number(v.unit_price ?? 0) || 0,
          });
        });
      });
    });

    return map;
  }

  // Method: 隠し項目から復帰
  private restoreFromHidden(): void
  {
    if (!this.mHiddenJson) return;

    const raw = (this.mHiddenJson.value ?? '').trim();
    if (!raw) return;

    try
    {
      const arr = JSON.parse(raw) as Array<any>;
      if (!Array.isArray(arr)) return;

      arr.forEach((x) =>
      {
        const id = typeof x?.id === 'string' ? x.id : '';
        const c = Number(x?.count ?? 0);

        if (!id) return;
        if (!Number.isFinite(c) || c <= 0) return;

        this.mCounts.set(id, Math.floor(c));
      });
    } catch
    {
      // 破損していても止めない
    }
  }

  // Method: カテゴリを検証
  private isValidCategory(category: string): category is ItemCategory
  {
    return category === 'appliance' || category === 'furniture' || category === 'box' || category === 'other';
  }

  // Method: 数値フォーマット
  private formatNumber(n: number): string
  {
    return n.toLocaleString('ja-JP');
  }

  // Method: HTMLエスケープ
  private escapeHtml(str: string): string
  {
    return str
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  // Method: HTML属性エスケープ
  private escapeAttr(str: string): string
  {
    return this.escapeHtml(str);
  }
}

// ==============================
// AREA: オプション（グループごとに1つ選択）
// ==============================
type OptionGroup = {
  id: string;
  title: string;
  choices: Array<{
    id: string;
    label: string;
    price: number;
  }>;
};

const OPTION_CATALOG: OptionGroup[] = [
  {
    id: 'bed',
    title: 'ベッド',
    choices: [
      { id: 'building', label: '組み立て', price: 2500 },
      { id: 'demolition', label: '解体', price: 1500 },
    ],
  },
  {
    id: 'washer_type_drum',
    title: 'ドラム式洗濯機',
    choices: [
      { id: 'attachment', label: '取り付け', price: 8000 },
      { id: 'remove', label: '取り外し', price: 4000 },
    ],
  },
  {
    id: 'washer_type_vertical',
    title: '縦型式洗濯機',
    choices: [
      { id: 'attachment', label: '取り付け', price: 1000 },
      { id: 'remove', label: '取り外し', price: 1000 },
    ],
  },
  {
    id: 'heavy_loads_addition',
    title: '重量物加算',
    choices: [
      { id: '90kg_or_more', label: '90kg以上有り', price: 10000 },
    ],
  },
  {
    id: 'furniture',
    title: '家具',
    choices: [
      { id: 'building', label: '組み立て', price: 5000 },
      { id: 'demolition', label: '解体', price: 3000 },
    ],
  },
  {
    id: 'other',
    title: 'その他',
    choices: [
      { id: 'consultation_required', label: '要相談', price: 0 },
    ],
  },
];

/**
 * // Class Obj: オプション（チェックボックス式：複数選択OK）
 * リファクタ方針
 * - 価格が 0 の場合は表示しない（UI/明細とも）
 * - total 表示は “円付き + 3桁区切り” に統一
 * - buildOptionsDetailText() のロジックを簡潔化（group順をカタログ順に維持）
 * - null 安全性と早期returnを整理
 */
class EstimateFormOptionsSelector
{
  private readonly mRoot: HTMLElement;

  private mOptionsRoot: HTMLElement | null = null;
  private mHiddenJson: HTMLInputElement | null = null;
  private mHiddenTotal: HTMLInputElement | null = null;
  private mTotalView: HTMLElement | null = null;

  // key: `${group_id}:${choice_id}`
  private mChecked: Set<string> = new Set();

  private mHiddenDetailText: HTMLInputElement | null = null;

  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  public init(): void
  {
    this.mOptionsRoot = this.mRoot.querySelector<HTMLElement>('.js-options-root');
    this.mHiddenJson = this.mRoot.querySelector<HTMLInputElement>('.js-options-json');
    this.mHiddenTotal = this.mRoot.querySelector<HTMLInputElement>('.js-options-total');
    this.mTotalView = this.mRoot.querySelector<HTMLElement>('.js-options-total-view');
    this.mHiddenDetailText = this.mRoot.querySelector<HTMLInputElement>('.js-options-detail-text');

    if (!this.mOptionsRoot || !this.mHiddenJson || !this.mHiddenTotal) return;

    this.restoreFromHidden();
    this.render();
    this.syncHidden();

    // change委譲（checkbox）
    this.mOptionsRoot.addEventListener('change', (ev: Event) =>
    {
      const target = ev.target as HTMLElement | null;
      if (!target) return;

      const input = target.closest('.js-option-checkbox') as HTMLInputElement | null;
      if (!input) return;

      const group_id = (input.dataset.groupId ?? '').trim();
      const choice_id = (input.dataset.choiceId ?? '').trim();
      if (!group_id || !choice_id) return;

      const key = this.makeKey(group_id, choice_id);

      if (input.checked)
      {
        this.mChecked.add(key);
      } else
      {
        this.mChecked.delete(key);
      }

      this.syncHidden();
    });
  }

  // Method: 描画
  private render(): void
  {
    if (!this.mOptionsRoot) return;

    const html = OPTION_CATALOG.map((group) =>
    {
      const choices_html = group.choices.map((c) =>
      {
        const key = this.makeKey(group.id, c.id);
        const is_checked = this.mChecked.has(key);

        // 価格 0 は表示しない
        const price_html = (c.price > 0)
          ? `<span class="price">${this.formatYen(c.price)}</span>`
          : '';

        return `
          <label class="check-field">
            <input
              type="checkbox"
              class="input-checkbox js-option-checkbox"
              data-group-id="${this.escapeAttr(group.id)}"
              data-choice-id="${this.escapeAttr(c.id)}"
              ${is_checked ? 'checked' : ''}
            >
            <span class="indicator" aria-hidden="true"></span>
            <span class="name">${this.escapeHtml(c.label)}</span>
            ${price_html}
          </label>
        `;
      }).join('');

      return `
        <div class="item" data-group="${this.escapeAttr(group.id)}">
          <p class="option-name ga-title">${this.escapeHtml(group.title)}</p>
          <div class="option-choices ga-text">
            ${choices_html}
          </div>
        </div>
      `;
    }).join('');

    this.mOptionsRoot.innerHTML = html;
  }

  // Method: 隠し項目同期
  private syncHidden(): void
  {
    if (!this.mHiddenJson || !this.mHiddenTotal) return;

    const payload: Array<{
      group_id: string;
      group_title: string;
      choice_id: string;
      choice_label: string;
      price: number;
    }> = [];

    let total = 0;

    OPTION_CATALOG.forEach((g) =>
    {
      g.choices.forEach((c) =>
      {
        const key = this.makeKey(g.id, c.id);
        if (!this.mChecked.has(key)) return;

        payload.push({
          group_id: g.id,
          group_title: g.title,
          choice_id: c.id,
          choice_label: c.label,
          price: c.price,
        });

        total += c.price;
      });
    });

    this.mHiddenJson.value = JSON.stringify(payload);
    this.mHiddenTotal.value = String(total);

    if (this.mTotalView)
    {
      // 表示は “円付き + 3桁区切り”
      this.mTotalView.textContent = this.formatYen(total);
    }

    this.mRoot.dispatchEvent(new CustomEvent('ef:optionsUpdated', {
      detail: { options: payload, total },
    }));

    if (this.mHiddenDetailText)
    {
      this.mHiddenDetailText.value = this.buildOptionsDetailText(payload, total);
    }
  }

  private buildOptionsDetailText(payload: Array<{
    group_id: string;
    group_title: string;
    choice_id: string;
    choice_label: string;
    price: number;
  }>, total: number): string
  {
    if (!payload || payload.length === 0) return '（未選択）';

    // group_id => items
    const grouped = new Map<string, Array<{ label: string; price: number; }>>();

    payload.forEach((x) =>
    {
      if (!grouped.has(x.group_id))
      {
        grouped.set(x.group_id, []);
      }
      grouped.get(x.group_id)?.push({ label: x.choice_label, price: x.price });
    });

    const lines: string[] = [];

    // 表示順はカタログ順（UIと一致）
    OPTION_CATALOG.forEach((g) =>
    {
      const items = grouped.get(g.id);
      if (!items || items.length === 0) return;

      lines.push(`${g.title}：`);

      items.forEach((i) =>
      {
        // 価格 0 は表示しない
        const price_text = (i.price > 0) ? `（${this.formatYen(i.price)}）` : '';
        lines.push(`- ${i.label}${price_text}`);
      });

      lines.push('');
    });

    lines.push(`小計：${this.formatYen(total)}`);

    return lines.join('\n').trim();
  }

  // Method: 隠し項目から復元
  private restoreFromHidden(): void
  {
    if (!this.mHiddenJson) return;

    const raw = (this.mHiddenJson.value ?? '').trim();
    if (!raw) return;

    try
    {
      const arr = JSON.parse(raw) as Array<{ group_id: string; choice_id: string; }>;
      if (!Array.isArray(arr)) return;

      arr.forEach((x) =>
      {
        const group_id = typeof x?.group_id === 'string' ? x.group_id : '';
        const choice_id = typeof x?.choice_id === 'string' ? x.choice_id : '';
        if (!group_id || !choice_id) return;

        const group = OPTION_CATALOG.find((g) => g.id === group_id);
        if (!group) return;

        const choice_exists = group.choices.some((c) => c.id === choice_id);
        if (!choice_exists) return;

        this.mChecked.add(this.makeKey(group_id, choice_id));
      });
    } catch
    {
      // 破損していても止めない
    }
  }

  private makeKey(group_id: string, choice_id: string): string
  {
    return `${group_id}:${choice_id}`;
  }

  /**
   * 数値をフォーマット（3桁区切り + 円）
   * 例: 12000 => "12,000円"
   */
  private formatYen(n: number): string
  {
    return `${n.toLocaleString('ja-JP')}円`;
  }

  private escapeHtml(str: string): string
  {
    return str
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  private escapeAttr(str: string): string
  {
    return this.escapeHtml(str);
  }
}



// ==============================
// 資材（数量カウンター＋小計）
// ==============================
type MaterialItem = {
  id: string;
  label: string;
  unit_price: number;
};

const MATERIAL_CATALOG: MaterialItem[] = [
  { id: 'mat_box_s', label: '段ボールS（3辺合計95cm以下）', unit_price: 200 },
  { id: 'mat_box_m', label: '段ボールM（3辺合計112cm以下）', unit_price: 250 },
  { id: 'mat_box_l', label: '段ボールL（3辺合計140cm以下）', unit_price: 300 },
  { id: 'mat_box_ll', label: '段ボールLL（3辺合計170cm以下）', unit_price: 400 },
];

/**
 * // Class Obj: 資材（数量カウンター＋小計）
 */
class EstimateFormMaterialsCounter
{
  private readonly mRoot: HTMLElement;

  private mMaterialsRoot: HTMLElement | null = null;
  private mHiddenJson: HTMLInputElement | null = null;
  private mHiddenTotal: HTMLInputElement | null = null;
  private mTotalView: HTMLElement | null = null;

  private mCounts: Map<string, number> = new Map();

  private mHiddenDetailText: HTMLInputElement | null = null;

  // Constructor: コンストラクタ
  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  // Method: 初期化
  public init(): void
  {
    this.mMaterialsRoot = this.mRoot.querySelector<HTMLElement>('.js-materials-root');
    this.mHiddenJson = this.mRoot.querySelector<HTMLInputElement>('.js-materials-json');
    this.mHiddenTotal = this.mRoot.querySelector<HTMLInputElement>('.js-materials-total');
    this.mTotalView = this.mRoot.querySelector<HTMLElement>('.js-materials-total-view');

    this.mHiddenDetailText = this.mRoot.querySelector<HTMLInputElement>('.js-materials-detail-text');

    if (!this.mMaterialsRoot || !this.mHiddenJson || !this.mHiddenTotal) return;

    MATERIAL_CATALOG.forEach((m) => this.mCounts.set(m.id, 0));
    this.restoreFromHidden();
    this.render();

    this.mMaterialsRoot.addEventListener('click', (ev: MouseEvent) =>
    {
      const target = ev.target as HTMLElement | null;
      if (!target) return;

      const dec = target.closest('.js-mat-dec') as HTMLButtonElement | null;
      if (dec)
      {
        ev.preventDefault();
        const id = (dec.dataset.id ?? '').trim();
        if (!id) return;

        this.changeCount(id, -1);
        return;
      }

      const inc = target.closest('.js-mat-inc') as HTMLButtonElement | null;
      if (inc)
      {
        ev.preventDefault();
        const id = (inc.dataset.id ?? '').trim();
        if (!id) return;

        this.changeCount(id, +1);
        return;
      }
    });

    this.syncHidden();
  }

  // Method: 描画
  private render(): void
  {
    if (!this.mMaterialsRoot) return;

    const html = MATERIAL_CATALOG.map((m) =>
    {
      const qty = this.mCounts.get(m.id) ?? 0;

      return `
        <div class="item" data-id="${this.escapeAttr(m.id)}">
          <div class="label">${this.escapeHtml(m.label)}</div>
          <div class="price">
            <span class="number">${this.formatYen(m.unit_price)}</span>
            <span class="symbol">✕</span>
          </div>
          <div class="counter">
            <button type="button" class="btn-decrement js-mat-dec" data-id="${this.escapeAttr(m.id)}" aria-label="減らす">
              <span class="symbol">－</span>
            </button>
            <span class="count js-mat-qty" data-id="${this.escapeAttr(m.id)}">${qty}</span>
            <button type="button" class="btn-increment js-mat-inc" data-id="${this.escapeAttr(m.id)}" aria-label="増やす">
              <span class="symbol">＋</span>
            </button>
          </div>
        </div>
      `;
    }).join('');

    this.mMaterialsRoot.innerHTML = html;
  }

  // Method: 数量変更
  private changeCount(id: string, delta: number): void
  {
    const current = this.mCounts.get(id) ?? 0;
    const next = Math.max(0, current + delta);

    this.mCounts.set(id, next);

    const qty_elems = this.mRoot.querySelectorAll<HTMLElement>(`.js-mat-qty[data-id="${CSS.escape(id)}"]`);
    qty_elems.forEach((el) =>
    {
      el.textContent = String(next);
    });

    this.syncHidden();
  }

  // Method: 隠し項目同期
  private syncHidden(): void
  {
    if (!this.mHiddenJson || !this.mHiddenTotal) return;

    const payload: Array<{
      id: string;
      label: string;
      unit_price: number;
      qty: number;
      subtotal: number;
    }> = [];

    let total = 0;

    MATERIAL_CATALOG.forEach((m) =>
    {
      const qty = this.mCounts.get(m.id) ?? 0;
      if (qty <= 0) return;

      const subtotal = qty * m.unit_price;

      payload.push({
        id: m.id,
        label: m.label,
        unit_price: m.unit_price,
        qty,
        subtotal,
      });

      total += subtotal;
    });

    this.mHiddenJson.value = JSON.stringify(payload);
    this.mHiddenTotal.value = String(total);

    if (this.mTotalView)
    {
      this.mTotalView.textContent = String(total);
    }

    this.mRoot.dispatchEvent(new CustomEvent('ef:materialsUpdated', {
      detail: { materials: payload, total },
    }));

    if (this.mHiddenDetailText)
    {
      this.mHiddenDetailText.value = this.buildMaterialsDetailText(payload, total);
    }
  }

  private buildMaterialsDetailText(payload: Array<{
    id: string;
    label: string;
    unit_price: number;
    qty: number;
    subtotal: number;
  }>, total: number): string
  {
    if (!payload || payload.length === 0) return '（未選択）';

    const lines: string[] = [];

    payload.forEach((x) =>
    {
      const unit = x.unit_price.toLocaleString('ja-JP');
      const sub = x.subtotal.toLocaleString('ja-JP');
      lines.push(`- ${x.label} ${unit}円 ×${x.qty} = ${sub}円`);
    });

    lines.push('');
    lines.push(`小計：${total.toLocaleString('ja-JP')}円`);

    return lines.join('\n').trim();
  }


  // Method: 隠し項目から復元
  private restoreFromHidden(): void
  {
    if (!this.mHiddenJson) return;

    const raw = (this.mHiddenJson.value ?? '').trim();
    if (!raw) return;

    try
    {
      const arr = JSON.parse(raw) as Array<{ id: string; qty: number; }>;
      if (!Array.isArray(arr)) return;

      arr.forEach((x) =>
      {
        if (!x || typeof x.id !== 'string') return;

        const qty = Number(x.qty);
        if (!Number.isFinite(qty) || qty <= 0) return;

        if (!MATERIAL_CATALOG.some((m) => m.id === x.id)) return;

        this.mCounts.set(x.id, Math.floor(qty));
      });
    } catch
    {
      // 破損していても止めない
    }
  }

  // Method: 金額表示
  private formatYen(n: number): string
  {
    return `${n.toLocaleString('ja-JP')}円`;
  }

  // Method: HTMLエスケープ
  private escapeHtml(str: string): string
  {
    return str
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  // Method: 属性エスケープ
  private escapeAttr(str: string): string
  {
    return this.escapeHtml(str);
  }
}

// ==============================
// 合計（grand_total）
// ==============================
/**
 * // Class Obj: 合計を計算するクラス
 */
class EstimateFormTotalCalculator
{
  private readonly mRoot: HTMLElement;

  private mDistanceView: HTMLElement | null = null;
  private mItemsView: HTMLElement | null = null;
  private mOptionsView: HTMLElement | null = null;
  private mMaterialsView: HTMLElement | null = null;
  private mGrandView: HTMLElement | null = null;

  private mHiddenGrand: HTMLInputElement | null = null;

  private mDistanceKm = 0;
  private mItemsCount = 0;
  private mItemsTotalPrice = 0;
  private mOptionsTotal = 0;
  private mMaterialsTotal = 0;

  // Constructor: コンストラクタ
  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  // Method: 初期化
  public init(): void
  {
    this.mDistanceView = this.mRoot.querySelector<HTMLElement>('.js-total-distance-view');
    this.mItemsView = this.mRoot.querySelector<HTMLElement>('.js-total-items-view');
    this.mOptionsView = this.mRoot.querySelector<HTMLElement>('.js-total-options-view');
    this.mMaterialsView = this.mRoot.querySelector<HTMLElement>('.js-total-materials-view');
    this.mGrandView = this.mRoot.querySelector<HTMLElement>('.js-total-grand-view');

    this.mHiddenGrand = this.mRoot.querySelector<HTMLInputElement>('.js-grand-total');

    this.render();

    // items
    this.mRoot.addEventListener('ef:itemsUpdated', (ev: Event) =>
    {
      const e = ev as CustomEvent<{ total_count: number; total_price: number; }>;
      this.mItemsCount = Number(e.detail?.total_count ?? 0);
      this.mItemsTotalPrice = Number(e.detail?.total_price ?? 0);
      this.render();
    });

    // options
    this.mRoot.addEventListener('ef:optionsUpdated', (ev: Event) =>
    {
      const e = ev as CustomEvent<{ total: number; }>;
      this.mOptionsTotal = Number(e.detail?.total ?? 0);
      this.render();
    });

    // materials
    this.mRoot.addEventListener('ef:materialsUpdated', (ev: Event) =>
    {
      const e = ev as CustomEvent<{ total: number; }>;
      this.mMaterialsTotal = Number(e.detail?.total ?? 0);
      this.render();
    });
  }

  // Method: 描画
  private render(): void
  {
    if (this.mDistanceView) this.mDistanceView.textContent = String(this.mDistanceKm);
    if (this.mItemsView) this.mItemsView.textContent = String(this.mItemsCount);
    if (this.mOptionsView) this.mOptionsView.textContent = String(this.mOptionsTotal);
    if (this.mMaterialsView) this.mMaterialsView.textContent = String(this.mMaterialsTotal);

    // ✅合計：オプション + 資材 +（家財金額があるなら）家財
    const grand = this.mOptionsTotal + this.mMaterialsTotal + this.mItemsTotalPrice;

    if (this.mGrandView) this.mGrandView.textContent = String(grand);

    if (this.mHiddenGrand)
    {
      this.mHiddenGrand.value = String(grand);
    }
  }
}

//////////////////////////////////////////////////
/// Class Obj: フリー入力
//////////////////////////////////////////////////
type FreeItem = {
  id: string;
  name: string;
  w_cm: string; // 数字でもOKですが、とりあえず文字列で保持（入力そのまま）
  d_cm: string;
  h_cm: string;
  qty: number;
};

/**
 * // Class Obj: フリー入力（※表示/非表示は CollapsePanel 側に委譲）
 */
class EstimateFormItemsFreeEditor
{
  private readonly mRoot: HTMLElement;

  private mGrid: HTMLElement | null = null;

  private mHiddenJson: HTMLInputElement | null = null;
  private mHiddenTotal: HTMLInputElement | null = null;

  private mItems: FreeItem[] = [];

  private mHiddenDetailText: HTMLInputElement | null = null;

  // Constructor
  constructor(root: HTMLElement)
  {
    this.mRoot = root;
  }

  public init(): void
  {
    this.mGrid = this.mRoot.querySelector<HTMLElement>('.js-items-free-grid');

    this.mHiddenJson = this.mRoot.querySelector<HTMLInputElement>('.js-items-free-json');
    this.mHiddenTotal = this.mRoot.querySelector<HTMLInputElement>('.js-items-free-count-total');

    this.mHiddenDetailText = this.mRoot.querySelector<HTMLInputElement>('.js-items-free-detail-text');

    if (!this.mGrid) return;

    // 復元 → 初期カード生成
    this.restoreFromHidden();
    if (this.mItems.length === 0)
    {
      this.mItems = this.makeDefaultItems(5);
    }

    this.render();
    this.syncHidden();
    this.bindEvents();
  }

  private bindEvents(): void
  {
    if (!this.mGrid) return;

    // +/-（イベント委譲）
    this.mGrid.addEventListener('click', (ev: MouseEvent) =>
    {
      const target = ev.target as HTMLElement | null;
      if (!target) return;

      const dec = target.closest('.js-free-dec') as HTMLButtonElement | null;
      if (dec)
      {
        ev.preventDefault();
        const id = (dec.dataset.id ?? '').trim();
        if (!id) return;
        this.changeQty(id, -1);
        return;
      }

      const inc = target.closest('.js-free-inc') as HTMLButtonElement | null;
      if (inc)
      {
        ev.preventDefault();
        const id = (inc.dataset.id ?? '').trim();
        if (!id) return;
        this.changeQty(id, +1);
        return;
      }
    });

    // 入力（イベント委譲）
    this.mGrid.addEventListener('input', (ev: Event) =>
    {
      const input = ev.target as HTMLInputElement | null;
      if (!input) return;

      const wrap = input.closest('.js-free-item') as HTMLElement | null;
      if (!wrap) return;

      const id = (wrap.dataset.id ?? '').trim();
      if (!id) return;

      const key = (input.dataset.key ?? '').trim();

      const item = this.mItems.find((x) => x.id === id);
      if (!item) return;

      if (key === 'name') item.name = input.value ?? '';
      if (key === 'w') item.w_cm = input.value ?? '';
      if (key === 'd') item.d_cm = input.value ?? '';
      if (key === 'h') item.h_cm = input.value ?? '';

      this.syncHidden();
    });
  }

  private changeQty(id: string, delta: number): void
  {
    const item = this.mItems.find((x) => x.id === id);
    if (!item) return;

    const next = Math.max(0, (item.qty ?? 0) + delta);
    item.qty = next;

    // 表示反映
    const qty_elems = this.mRoot.querySelectorAll<HTMLElement>(`.js-free-qty[data-id="${CSS.escape(id)}"]`);
    qty_elems.forEach((el) => { el.textContent = String(next); });

    this.syncHidden();
  }

  private render(): void
  {
    if (!this.mGrid) return;

    const html = this.mItems.map((x) =>
    {
      const qty = Number.isFinite(x.qty) ? x.qty : 0;

      return `
        <div class="item js-free-item" data-id="${this.escapeAttr(x.id)}">
          <div class="object">
            <label class="name" for="free_${this.escapeAttr(x.id)}_name">品目</label>
            <input
              id="free_${this.escapeAttr(x.id)}_name"
              type="text"
              class="ef-items-freeCard__input"
              placeholder="品目名を入力してください"
              value="${this.escapeAttr(x.name ?? '')}"
              data-key="name"
            >
          </div>

          <div class="dimensions">
            <div class="size">
              <label class="name" for="free_${this.escapeAttr(x.id)}_w">幅</label>
              <input
                id="free_${this.escapeAttr(x.id)}_w"
                type="text"
                class="ef-items-freeDim__input"
                placeholder="100 cm"
                value="${this.escapeAttr(x.w_cm ?? '')}"
                data-key="w"
              >
            </div>

            <div class="size">
              <label class="name" for="free_${this.escapeAttr(x.id)}_d">奥行</label>
              <input
                id="free_${this.escapeAttr(x.id)}_d"
                type="text"
                class="ef-items-freeDim__input"
                placeholder="100 cm"
                value="${this.escapeAttr(x.d_cm ?? '')}"
                data-key="d"
              >
            </div>

            <div class="size">
              <label class="name" for="free_${this.escapeAttr(x.id)}_h">高さ</label>
              <input
                id="free_${this.escapeAttr(x.id)}_h"
                type="text"
                class="ef-items-freeDim__input"
                placeholder="100 cm"
                value="${this.escapeAttr(x.h_cm ?? '')}"
                data-key="h"
              >
            </div>

          </div>

          <div class="quantity">
            <label class="name" for="free_${this.escapeAttr(x.id)}_qty">個数</label>

            <div class="counter">
              <button type="button" class="btn-decrement js-free-dec" data-id="${this.escapeAttr(x.id)}" aria-controls="free_${this.escapeAttr(x.id)}_qty" aria-label="減らす">
              <span class="symbol">－</span>
              </button>

              <output id="free_${this.escapeAttr(x.id)}_qty" class="count js-free-qty" data-id="${this.escapeAttr(x.id)}">${qty}</output>

              <button type="button" class="btn-increment js-free-inc" data-id="${this.escapeAttr(x.id)}" aria-controls="free_${this.escapeAttr(x.id)}_qty" aria-label="増やす">
              <span class="symbol">＋</span>
              </button>
            </div>
          </div>

        </div>
      `;
    }).join('');

    this.mGrid.innerHTML = html;
  }

  private syncHidden(): void
  {
    const payload = this.mItems
      .map((x) =>
      {
        const qty = Number.isFinite(x.qty) ? x.qty : 0;
        return {
          id: x.id,
          name: (x.name ?? '').trim(),
          w_cm: (x.w_cm ?? '').trim(),
          d_cm: (x.d_cm ?? '').trim(),
          h_cm: (x.h_cm ?? '').trim(),
          qty,
        };
      })
      .filter((x) =>
      {
        if (x.qty > 0) return true;
        if (x.name) return true;
        if (x.w_cm || x.d_cm || x.h_cm) return true;
        return false;
      });

    const total_count = payload.reduce((sum, x) => sum + (x.qty ?? 0), 0);

    if (this.mHiddenJson) this.mHiddenJson.value = JSON.stringify(payload);
    if (this.mHiddenTotal) this.mHiddenTotal.value = String(total_count);

    this.mRoot.dispatchEvent(new CustomEvent('ef:itemsFreeUpdated', {
      detail: { items: payload, total_count },
    }));

    if (this.mHiddenDetailText)
    {
      this.mHiddenDetailText.value = this.buildFreeItemsDetailText(payload);
    }
  }

  private buildFreeItemsDetailText(payload: Array<{
    id: string;
    name: string;
    w_cm: string;
    d_cm: string;
    h_cm: string;
    qty: number;
  }>): string
  {
    if (!payload || payload.length === 0) return '（入力なし）';

    const lines: string[] = [];
    let n = 0;

    payload.forEach((x) =>
    {
      n += 1;

      const name = (x.name ?? '').trim() || '（品目名未入力）';
      const w = (x.w_cm ?? '').trim();
      const d = (x.d_cm ?? '').trim();
      const h = (x.h_cm ?? '').trim();
      const qty = Number.isFinite(x.qty) ? x.qty : 0;

      const dims_parts: string[] = [];
      if (w) dims_parts.push(`幅:${w}`);
      if (d) dims_parts.push(`奥行:${d}`);
      if (h) dims_parts.push(`高さ:${h}`);

      const dims = dims_parts.length > 0 ? dims_parts.join(' ') : '寸法なし';

      lines.push(`${n}) ${name} / ${dims} / 個数:${qty}`);
    });

    return lines.join('\n').trim();
  }


  private restoreFromHidden(): void
  {
    if (!this.mHiddenJson) return;

    const raw = (this.mHiddenJson.value ?? '').trim();
    if (!raw) return;

    try
    {
      const arr = JSON.parse(raw) as Array<any>;
      if (!Array.isArray(arr)) return;

      const restored: FreeItem[] = [];

      arr.forEach((x, i) =>
      {
        const id = typeof x?.id === 'string' ? x.id : `free_${i + 1}`;
        const qty = Number(x?.qty ?? 0);

        restored.push({
          id,
          name: typeof x?.name === 'string' ? x.name : '',
          w_cm: typeof x?.w_cm === 'string' ? x.w_cm : '',
          d_cm: typeof x?.d_cm === 'string' ? x.d_cm : '',
          h_cm: typeof x?.h_cm === 'string' ? x.h_cm : '',
          qty: Number.isFinite(qty) ? Math.max(0, Math.floor(qty)) : 0,
        });
      });

      this.mItems = restored;
    } catch
    {
      // 破損していても止めない
    }
  }

  private makeDefaultItems(n: number): FreeItem[]
  {
    return Array.from({ length: n }).map((_, i) =>
    ({
      id: `free_${i + 1}`,
      name: '',
      w_cm: '',
      d_cm: '',
      h_cm: '',
      qty: 0,
    }));
  }

  private escapeHtml(str: string): string
  {
    return str
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  private escapeAttr(str: string): string
  {
    return this.escapeHtml(str);
  }
}





// ==============================
// AREA: 起動
// ==============================
document.addEventListener('DOMContentLoaded', () =>
{
  const root = document.querySelector<HTMLElement>('#areaEstimateForm');
  if (!root) return;

  new EstimateFormAddressAutoFill(root).init();
  new EstimateFormSchedulePicker(root).init();
  new EstimateFormItemCounter(root).init();
  new EstimateFormOptionsSelector(root).init();
  new EstimateFormMaterialsCounter(root).init();
  new EstimateFormTotalCalculator(root).init();
  // new EstimateFormFreeItemsEditor(root).init();
  new EstimateFormItemsFreeEditor(root).init();

});

//////////////////////////////////////////////////
/// AREA: end
//////////////////////////////////////////////////